import React, { useEffect, useState, useMemo, useCallback } from 'react'
import Box from '@mui/material/Box'
import { editObject, getObject, addObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import Form from '../../form/Form'
import { getProductFields } from './ProductFields'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import Toolbar from '../../toolbar/Toolbar'
import { handleProductImagesUpload } from '../../../utils/documentManager'

const ROUTES = {
  EDIT: '/products/edit',
  VIEW: '/products/view',
  ADD: '/products/add'
}

const buildPayload = ({ data, user, mode }) => {
  const isEdit = mode === 'edit'

  const sanitizedData = {
    ...data,
    ...(data?.customer_type === 2 ? { siret: null, siren: null } : {})
  }

  return {
    ...sanitizedData,
    ...(isEdit
      ? { lastModBy: user.email }
      : {
          owner_id: user.company_id,
          company_id: user.company_id,
          createdBy: user.email
        })
  }
}

const EditProduct = () => {
  const [product, setProduct] = useState({ options: [] })
  const [isSubmit, setIsSubmit] = useState(false)
  const history = useNavigate()
  const { user } = useAuth()
  const location = useLocation()
  const [file, setFile] = useState([])
  const path = location.pathname
  const productId = location.state?.id

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  // ✅ SIMPLE: Juste une fonction qui met à jour product.options
  // MyTableEditable gère toute la complexité en interne
  const setProductOptions = useCallback((payload) => {
    setProduct((prev) => {
      // Support updater function
      if (typeof payload === 'function') {
        const result = payload(prev)
        if (Array.isArray(result)) {
          return { ...prev, options: result }
        }
        return result
      }

      // Support direct array
      if (Array.isArray(payload)) {
        return { ...prev, options: payload }
      }

      // Support object with .options
      if (payload && payload.options) {
        return { ...prev, options: payload.options }
      }

      return prev
    })
  }, [])

  const productQuery = mode === 'edit' ? { key: 'product', fn: getObject, args: ['products', productId] } : null

  const queries = [productQuery].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'products',
    mode,
    id: productId,
    user,
    addFn: addObject,
    editFn: editObject,
    updateCounter: false,
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: () => setIsSubmit(false),
    onError: () => setIsSubmit(false),
    buildPayload
  })

  const memoizedFields = useMemo(() => getProductFields(product, setProductOptions), [product, setProductOptions])

  const options = useMemo(() => ({ fields: memoizedFields, data: product }), [memoizedFields, product])

  const toolbarOptions = useMemo(
    () => ({
      title: () => {
        return path.includes('/add') ? 'Nouveau Produit' : `Produit ${product.name}`
      }
    }),
    [product, path]
  )

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched.product) {
        setProduct(fetched.product)
        if (fetched.product.attachments) setFile(fetched.product.attachments.find((a) => a.type === 'image'))
      }
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    const uploadAndSave = async () => {
      if (file && file.length > 0) {
        await handleProductImagesUpload(file, user, product, setProduct)
      }

      setFile([])
      saveEntity({ data: product })
    }

    if (isSubmit) {
      uploadAndSave()
    }
  }, [isSubmit])

  useEffect(() => {
    if (product.file && product.file.length > 0) {
      const newPreviews = Array.from(product.file).map((f) => ({
        type: 'image',
        file: f,
        isPreview: true
      }))

      setProduct((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []).filter((a) => !a.isPreview), ...newPreviews],
        file: undefined
      }))

      setFile(product.file)
    }
  }, [product.file])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexBasis: 'auto',
        flexGrow: 1,
        flexShrink: 1
      }}
    >
      <Toolbar options={toolbarOptions} object={product} />

      <Form
        mode='edit'
        options={options}
        setDataParent={setProduct}
        setIsSubmitParent={setIsSubmit}
        loading={loading}
      />
    </Box>
  )
}

export default EditProduct
